import { getAppConfigSingleton } from "@expert/app-config";
import { useToast } from "@expert/common-ui";
import { getLogger } from "@expert/logging";
import { useCallback, useState } from "react";
import { shallow } from "zustand/shallow";
import { useVerifiedCustomerStore } from "@/customer-info";
import { usePartner, useSession } from "@/sdk";
import { getBackupGaiaSummary, getGaiaSummary } from "../api/sessionSummaryApiClient";
import { useSessionSummaryStore } from "../store/useSessionSummaryStore";
import { defaultAdditionalSummaryTimes } from "./utils";

const logger = getLogger({ module: "sessionSummary" });

const EMPTY_CUSTOMER: { accountNumber?: string } = {};

/**
 * Hook for session summary logic to separate from UI components
 */
export const useSummary = () => {
    const appConfig = getAppConfigSingleton();
    const activeSession = useSession();
    const partner = usePartner();
    const toast = useToast();

    const [summaryGenerated, setSummaryGenerated] = useState(false);

    const { summary, submitted, setSessionSummary } = useSessionSummaryStore((state) => state, shallow);
    const { accountNumber } = useVerifiedCustomerStore(
        (state) => state.getVerifiedCustomer(activeSession.id).customer || {},
    );

    const validAccountNumber =
        accountNumber !== undefined &&
        (appConfig.getPartnersConfig(partner).allowedAccountNumberLengths?.includes(accountNumber.length) ??
            EMPTY_CUSTOMER);

    const {
        requiresManualCopy,
        gaiaSummaryModel,
        sessionSummaryTimes = defaultAdditionalSummaryTimes,
    } = appConfig.getPartnersConfig(partner);

    const shouldSendSummary = !submitted && summary;

    const setSessionSummaryFromGaia = useCallback(
        async (setSummaryLoaded: (arg0: boolean) => void, agentCallSid?: string) => {
            logger.trace({ agentCallSid, sessionId: activeSession.id, partner }, "Gaia summary fetch check");

            const generatedSummary = agentCallSid
                ? await getGaiaSummary(
                      { callSid: agentCallSid, sessionId: activeSession.id, partner },
                      gaiaSummaryModel,
                  )
                : await getBackupGaiaSummary({ sessionId: activeSession.id, partner }, gaiaSummaryModel);

            if (!generatedSummary) {
                logger.warn("Received empty summary from Gaia");
            } else {
                setSessionSummary({ value: generatedSummary, autoGenerated: true });
                setSummaryGenerated(!!generatedSummary);
            }
            setSummaryLoaded(true);
        },
        [activeSession.id, partner, setSessionSummary, gaiaSummaryModel],
    );

    const onDismiss = useCallback(
        async (closeModal: () => void, agentCallSid?: string, { checkSummary = true } = {}) => {
            logger.trace("Session summary dismissed");
            closeModal();
            if (checkSummary && shouldSendSummary) {
                toast.success("Session summary copied to clipboard");
                try {
                    await navigator.clipboard.writeText(summary);
                } catch (err) {
                    logger.warn({ err }, "Session summary not copied to clipboard");
                }
            }
        },
        [shouldSendSummary, summary, toast],
    );

    const getExtraSummaryTime = useCallback(
        (canAutosend: boolean, hasSummaryOnly: boolean) => {
            if (canAutosend) {
                return sessionSummaryTimes.autosend;
            } else if (hasSummaryOnly) {
                return sessionSummaryTimes.manualCopy;
            }
            return sessionSummaryTimes.maximum;
        },
        [sessionSummaryTimes],
    );

    return {
        summaryGenerated,
        validAccountNumber,
        requiresManualCopy,
        sessionSummaryTimes,
        summary,
        setSessionSummaryFromGaia,
        onDismiss,
        getExtraSummaryTime,
    };
};
